import React, { useCallback, useEffect, useState } from "react";
import { useCSVReader } from "react-papaparse";

import { generateReferralWalletList, getAllReferralPayments, processReferralPayments } from "services";

import { goTo, saveFile, useCancelAsyncOnUnmount } from "utils";

import { AdminHead, AdminTable, Button, Col, Icon, MainPage, Row, Tooltip } from "components";

export const ReferralPaymentList = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [payments, setPayments] = useState([]);
  const signal = useCancelAsyncOnUnmount()();
  const { CSVReader } = useCSVReader();

  const loadAllPayments = useCallback(() => {
    getAllReferralPayments({
      signal,
    })
      .then((res) => {
        setPayments(res.length > 0 ? res : []);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [signal]);

  const openItem = (item) => {
    goTo(null, null, `${process.env.REACT_APP_FARM_URL}/credit-note/${item.token}`, true);
  };

  const generateList = () => {
    setSaving(true);

    generateReferralWalletList()
      .then((res) => saveFile(res, "referral-wallets.zip"))
      .finally(() => {
        setSaving(false);
      });
  };

  const processFile = (csv) => {
    setSaving(true);

    processReferralPayments({
      data: csv.data,
      signal,
    })
      .then(() => {
        loadAllPayments();
      })
      .finally(() => {
        setSaving(false);
      });
  };

  useEffect(() => {
    loadAllPayments();
  }, [loadAllPayments]);

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <AdminHead
        headline={`${payments.length} payment records`}
        description={
          <>
            View recorded payments here. Download the list of (non-zero) wallet funds to pay. In order to record payments,{" "}
            <b>upload the exact same summary file, with the amount_paid column filled.</b> An email with a credit note will be sent to each user who received a
            payment.
            <br />
            <br />
            <b>Partial payments are not supported (i.e. if a farmer is owed 40€, we pay them 40€).</b>
          </>
        }
        extra={
          <div className="right csv-uploader">
            <Tooltip placement="bottom" title="Generate a wallet list">
              <Button size="small" type="secondary" disabled={saving} loading={saving} onClick={() => generateList()}>
                <Icon iconName="tabler/wallet" size="s" />
              </Button>
            </Tooltip>
            <Tooltip placement="bottom" title="Upload payment list">
              <CSVReader
                onUploadAccepted={(v) => processFile(v)}
                noDrag
                style={{
                  dropArea: {
                    border: 0,
                    padding: 0,
                    margin: 0,
                    width: "auto",
                    height: "auto",
                    display: "inline-block",
                  },
                  progressBar: {
                    display: "none !important",
                    opacity: 0,
                  },
                }}
              >
                {({ getRootProps }) => (
                  <Button size="small" type="secondary" className="margin-left-50" disabled={saving} loading={saving} {...getRootProps()}>
                    <Icon iconName="tabler/upload" size="s" />
                  </Button>
                )}
              </CSVReader>
            </Tooltip>
          </div>
        }
      />
      <Row>
        <Col>
          {payments.length && (
            <AdminTable
              dataSource={payments}
              config={{
                exclude: ["utc", "farmId", "token", "userId"],
                actions: [
                  {
                    icon: "tabler/download",
                    func: openItem,
                    tooltip: "Download this file",
                  },
                  {
                    icon: "tabler/eye",
                    route: "/user/",
                    record: ["userId"],
                    tooltip: "View this farm",
                  },
                ],
              }}
            />
          )}
        </Col>
      </Row>
    </MainPage>
  );
};
